import { uniqBy } from 'lodash-es';

import { getUserThemes } from '../../../services/tagboards';
import { isTheme } from '../../../utils/production';

const state = {
  themes: [],
  lookupTable: {},

  // Track which themes are being loaded by ID
  isLoadingMap: {},

  isLoading: false,
  page: 1,
  limit: 10,
  total: 0,
  tags: [],
};

const actions = {
  /**
   * Get list of themes, paginated.
   */
  async getThemes(context) {
    const { production } = context.rootState.production;
    const { owner } = production;
    const { composer } = production;
    const { tags, page } = context.state;

    context.commit('SET_THEMES_FIELD', { key: 'isLoading', value: true });

    let themes;
    let total = 0;

    if (composer) {
      // Fetch ALL the themes
      ({ result: themes } = await getUserThemes(owner));
    } else {
      // Paginate the themes (for producer)
      const opts = { page };

      if (tags.length) {
        opts.tags = tags.join(',');
      }

      ({ themes, total } = await getUserThemes(owner, opts));
    }

    context.commit('SET_THEMES_FIELD', { key: 'isLoading', value: false });
    context.commit('SET_THEMES', themes);
    context.commit('ADD_THEMES_TO_LOOKUP_TABLE', themes);
    context.commit('SET_THEMES_FIELD', { key: 'total', value: total });
  },

  /**
   * Load themes currently in the production playlist.
   * This can be ran over and over and will only load what's missing in the lookup table.
   */
  async getTimelineThemes(context) {
    const { production } = context.rootState.production;
    const { owner } = production;
    const { timeline } = context.rootState.production.production;

    const themeIds = timeline.reduce((memo, post) => {
      const theme = context.state.lookupTable[post._id];

      // Ignore non-themes, and any themes that already loaded (and have `settings`)
      if (isTheme(post.layout) && !theme?.settings) {
        memo.push(post._id);
      }

      return memo;
    }, []);

    if (!themeIds.length) {
      return;
    }

    context.commit('SET_THEMES_LOADING', { ids: themeIds, loading: true });

    const { themes } = await getUserThemes(owner, {
      themes: themeIds.filter(Boolean).join(','),
    });

    context.commit('ADD_THEMES_TO_LOOKUP_TABLE', themes);
    context.commit('SET_THEMES_LOADING', { ids: themeIds, loading: false });
  },

  /**
   * Increment page number and fetch some more themes
   */
  incrementThemesPage(context) {
    context.commit('SET_THEMES_FIELD', { key: 'page', value: context.state.page + 1 });
    context.dispatch('getThemes');
  },

  setThemes(context, payload) {
    context.commit('SET_THEMES', payload);
  },

  /**
   * Clear current themes array and reset pagination
   */
  resetThemes(context) {
    context.commit('RESET_THEMES');
  },

  addThemeTag(context, tag) {
    context.commit('ADD_THEME_TAG', tag);
    context.dispatch('resetThemes');
    context.dispatch('getThemes');
  },

  removeThemeTag(context, tag) {
    context.commit('REMOVE_THEME_TAG', tag);
    context.dispatch('resetThemes');
    context.dispatch('getThemes');
  },

  /**
   * Clear any tags used to filter results, reset page to 1, then refetch themes
   */
  clearThemeTags(context) {
    context.commit('CLEAR_THEME_TAGS');
    context.dispatch('resetThemes');
    context.dispatch('getThemes');
  },
};

const mutations = {
  /* eslint-disable no-param-reassign, no-shadow */

  SET_THEMES_FIELD(state, { key, value }) {
    state[key] = value;
  },

  /**
   * Set loading flags for specific themes by ID,
   * so we know in the theme editor if a theme is still loading.
   */
  SET_THEMES_LOADING(state, { ids, loading }) {
    state.isLoadingMap = {
      ...state.isLoadingMap,
      ...ids.reduce((memo, id) => ({
        ...memo,
        [id]: loading,
      }), {}),
    };
  },

  SET_THEMES(state, themes) {
    state.themes = uniqBy(state.themes.concat(themes), '_id');
  },

  ADD_THEME_TAG(state, tag) {
    state.tags = [...state.tags, tag];
  },

  REMOVE_THEME_TAG(state, tag) {
    state.tags = state.tags.filter((t) => t !== tag);
  },

  CLEAR_THEME_TAGS(state) {
    state.tags = [];
  },

  RESET_THEMES(state) {
    state.themes = [];
    state.page = 1;
    state.total = 0;
  },

  ADD_THEMES_TO_LOOKUP_TABLE(state, themes) {
    state.lookupTable = {
      ...state.lookupTable,
      ...(themes.reduce((memo, theme) => ({
        ...memo,
        [theme._id]: {
          ...state.lookupTable[theme._id],
          ...theme,
        },
      }), {})),
    };
  },
};

const getters = {
  hasMoreThemes(state) {
    return state.total > (state.page * state.limit);
  },
};

export default {
  state,
  actions,
  mutations,
  getters,
};
